import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as Info } from '../../assets/img/icons/common/InfoOutlined.svg';
import { EventItem } from './EventItem/EventItem';
import { EVENTS_HISTORY_LIMIT } from '../HistoryPlayer/constants/event.constants';
import useHistoryEventsContext from '../HistoryPlayer/hooks/useHistoryEventsContext';
import Loading from '../Loading/Loading';

const PlayerActivityHistoryEvents = ({ events, loadMore, historyMode, streamsKey }) => {
  const { eventsOffsetIndexRef } = useHistoryEventsContext();
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [visibleEventIds, setVisibleEventIds] = useState(new Set());
  
  const rootRef = useRef(null);
  const observerRef = useRef(null);
  const observedElementsRef = useRef(new Map());
  const activityEvents = [];
  
  if (historyMode) {
    activityEvents.push(...events.data.slice(0, eventsOffsetIndexRef.current + EVENTS_HISTORY_LIMIT));
  } else {
    activityEvents.push(...events.data);
  }
  
  useEffect(() => {
    return () => {
      setSelectedEvent(null);
    };
  }, [historyMode, streamsKey]);
  
  useEffect(() => {
    const root = rootRef.current;
    
    if (!root) {
      return;
    }
    
    observerRef.current = new IntersectionObserver(
      entries => {
        setVisibleEventIds(previous => {
          const next = new Set(previous);
          
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const eventId = observedElementsRef.current.get(entry.target);
              
              if (eventId) {
                next.add(eventId);
              }
            }
          });
          
          return next;
        });
      },
      {
        root,
        rootMargin: '200px 0px',
      }
    );
    
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedElementsRef.current.clear();
    };
  }, []);
  
  if (events.loading) {
    return (
      <div ref={rootRef} id="scrollable-activity-body" className="activity-history__body">
        <Loading className="activity-history__loading" />
      </div>
    );
  }
  
  if (!activityEvents.length && !events.loading) {
    return (
      <div ref={rootRef} id="scrollable-activity-body" className="activity-history__body">
        <div className="activity-history__no-data">
          <Info />
          <span>No activity</span>
        </div>
      </div>
    );
  }
  
  let hasMore;
  
  if (historyMode) {
    hasMore = eventsOffsetIndexRef.current + EVENTS_HISTORY_LIMIT < events.data.length;
  } else {
    hasMore = events.existNext && !events.loading && !events.scrollLoading;
  }
  
  return (
    <div ref={rootRef} id="scrollable-activity-body" className="activity-history__body">
      <InfiniteScroll
        dataLength={activityEvents.length}
        next={loadMore}
        hasMore={hasMore}
        scrollableTarget="scrollable-activity-body"
        scrollThreshold={0.99}
        style={{ overflowY: 'hidden' }}
      >
        <div id="activity-history-list" className="activity-history__list">
          {activityEvents.map(event => (
            <EventItem
              key={event.uuid}
              rootRef={eventRef => {
                if (!eventRef) {
                  return;
                }
            
                observedElementsRef.current.set(eventRef, event.uuid);
                observerRef.current?.observe(eventRef);
              }}
              event={event}
              setSelectedEvent={setSelectedEvent}
              historyMode={historyMode}
              active={selectedEvent && selectedEvent.uuid === event.uuid}
              loadImage={visibleEventIds.has(event.uuid)}
            />
          ))}
      
          {events.scrollLoading ? (
            <div className="activity-history__next-data-loader">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          ) : null}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default PlayerActivityHistoryEvents;